import type { JobReasonInput } from '@chukua/common';
import { Button, Form, Input, Modal, Select } from 'antd';
import { useState } from 'react';
import { useJobReasonCodeOptions, type JobReasonCodeAction } from '../hooks/use-job-reason-code-options';

interface JobActionReasonModalProps {
  action: JobReasonCodeAction | null;
  title: string;
  onCancel: () => void;
  onSubmit: (input: JobReasonInput) => Promise<void>;
}

/** The reason-required modal for `hold`/`cancel` (A2, gitaumoses4/chukua#40) — split out of `JobStateActions` to stay under the per-function line limit. */
export function JobActionReasonModal({ action, title, onCancel, onSubmit }: JobActionReasonModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<JobReasonInput>();
  const reasonCodes = useJobReasonCodeOptions(action);

  async function submit(values: JobReasonInput) {
    setSubmitting(true);
    try {
      await onSubmit(values);
      form.resetFields();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={action !== null} title={title} onCancel={onCancel} footer={null} destroyOnClose>
      <Form<JobReasonInput> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="reasonCode" label="Reason code" rules={[{ required: true, message: 'Select a reason code' }]}>
          <Select
            loading={reasonCodes.status === 'loading'}
            placeholder="Select a reason code"
            options={reasonCodes.options.map((code) => ({ label: code.name, value: code.code }))}
          />
        </Form.Item>
        <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'Enter a reason' }]}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item className="mb-0 flex justify-end">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}
